import store from '@ps/store'
import { libTabNameList } from '@ps/store/workbench/resource-manager/config'
import { categoryList } from '@ps/store/workbench/resource-manager/config'
import {
  getMaskComponentNodeByChildNode,
  getSceneNodeByNode,
  isGraphicsNode,
  isGroup,
  isImage,
  isSequenceFrame,
} from '@ps/bridge/qc/base'
import qc from 'qc'
import { scripts } from '@ps/bridge/qc/scripts'
import { getShapeByNode } from '@ps/store/workbench/stage/utils/helper'
import { getDescendants } from '@ps/bridge/qc/node/utils'
import { EventBus, Event } from '@ps/utils/event-bus'
// import { resetSplitProp } from '@ps/views/workbench/layer-manager/layer'
import gameIframe from '@ps/bridge/iframe/iframe'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { nodeTypes } from '@ps/bridge/qc/scripts/editor'
import { executeAction, getAddNodeAction } from '@ps/workbench/undo-redo'
import {
  getPropByLayoutString,
  removeUnSplitProInLayout,
} from '@ps/bridge/qc/node/layout'
import nodePool from '@ps/bridge/qc/node-pool'
import i18n from '@ps/lang'
import { checkIsSupportMask } from '@ps/store/workbench/visual-program/utils'

export enum MaskShape {
  RECT = 'rect',
  ROUND = 'circular',
}

export enum MaskSettingTab {
  Design = 'design', // 外观
  Animation = 'animation', // 动画
  Event = 'event', // 事件
}

export function isResourceNode(node: qc.Node) {
  return isImage(node) || isSequenceFrame(node)
}

export const isMaskGroupNode = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const iframeWindow = gameIframe.iframe
  if (iframeWindow && iframeWindow.ps && iframeWindow.ps.MaskGroup) {
    return !!node.getScript(scripts.maskGroup.name)
  }
  return false
}
// 根据遮罩的子节点获取其遮罩组的节点
export function getMaskGroupNodeByChildNode(node: qc.Node) {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (!node) {
      return null
    } else if (node.getScript('ps.VPGamePlay')) {
      return null
    } else if (isMaskGroupNode(node)) {
      return node
    }
    node = node.parent
  }
}
// 形状
export const isMaskShapeNode = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const iframeWindow = gameIframe.iframe
  if (iframeWindow && iframeWindow.ps && iframeWindow.ps.MaskGroup) {
    const maskGroupNode = getMaskGroupNodeByChildNode(node)
    if (!maskGroupNode) {
      return false
    }
    const maskGroup = maskGroupNode?.getScript(scripts.maskGroup.name)
    return maskGroup.maskShape.uuid === node.uuid
  }
  return false
}
// 遮罩形状
export const isMaskShapeGroupNode = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const iframeWindow = gameIframe.iframe
  if (iframeWindow && iframeWindow.ps && iframeWindow.ps.MaskGroup) {
    const maskGroupNode = getMaskGroupNodeByChildNode(node)
    if (!maskGroupNode) {
      return false
    }
    const maskGroup = maskGroupNode?.getScript(scripts.maskGroup.name)
    return maskGroup.maskShapeLayer.uuid === node.uuid
  }
  return false
}

// 跟随元素组
export const isFollowElementNode = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const iframeWindow = gameIframe.iframe
  if (iframeWindow && iframeWindow.ps && iframeWindow.ps.MaskGroup) {
    const maskGroupNode = getMaskGroupNodeByChildNode(node)
    if (!maskGroupNode) {
      return false
    }
    const maskGroup = maskGroupNode?.getScript(scripts.maskGroup.name)
    return maskGroup.followElement.uuid === node.uuid
  }
  return false
}

// 是否被遮罩组节点
export const isMaskedGroupNode = (node: qc.Node): boolean => {
  if (!node || !node.parent) {
    return false
  }
  const iframeWindow = gameIframe.iframe
  if (iframeWindow && iframeWindow.ps && iframeWindow.ps.MaskGroup) {
    const maskGroup = node.parent.getScript(scripts.maskGroup.name)
    if (!maskGroup) {
      return false
    }
    return maskGroup.maskGroupLayer.uuid === node.uuid
  }
  return false
}

export const getMaskShapeNode = (node: qc.Node): qc.Node | null => {
  if (!node) {
    return null
  }
  const maskGroup = node.getScript(scripts.maskGroup.name)
  return maskGroup.maskShape
}

export const getMaskShapeAidNode = (node: qc.Node): qc.Node | null => {
  if (!node) {
    return null
  }
  const maskGroup = node.getScript(scripts.maskGroup.name)
  return maskGroup.maskShape.getChild('aidShape')
}

export const isMaskShapeAidNode = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const groupNode = getMaskGroupNodeByChildNode(node)
  if (!groupNode) return false
  const maskGroup = groupNode.getScript(scripts.maskGroup.name)
  return (
    maskGroup.maskShape.uuid === node.parent.uuid && node.name === 'aidShape'
  )
}
// 获取遮罩组里的跟随元素组
export const getMaskFollowElementNode = (node: qc.Node): qc.Node | null => {
  if (!node) {
    return null
  }
  const maskGroup = node.getScript(scripts.maskGroup.name)
  return maskGroup.followElement
}

// 跟随元素锚点计算
export const calFollowElementPivot = (node: qc.Node) => {
  const followElementNode = getMaskFollowElementNode(node)
  const maskShapeNode = getMaskShapeNode(node)
  const followLayout = followElementNode?.getScript(scripts.layout.name)
  const shapeLayout = maskShapeNode?.getScript(scripts.layout.name)
  let followLayoutX,
    followLayoutY,
    shapeLayoutX,
    shapeLayoutY,
    shapeLayoutWidth,
    shapeLayoutHeight
  if (followLayout && followLayout.layout) {
    followLayoutX = getPropByLayoutString('x', followLayout.layout.pdef)
    followLayoutY = getPropByLayoutString('y', followLayout.layout.pdef)
  } else {
    followLayoutX = followElementNode.x
    followLayoutY = followElementNode.y
  }
  if (shapeLayout && shapeLayout.layout) {
    shapeLayoutX = getPropByLayoutString('x', shapeLayout.layout.pdef)
    shapeLayoutY = getPropByLayoutString('y', shapeLayout.layout.pdef)
    shapeLayoutWidth = getPropByLayoutString('width', shapeLayout.layout.pdef)
    shapeLayoutHeight = getPropByLayoutString('height', shapeLayout.layout.pdef)
  } else {
    shapeLayoutX = maskShapeNode.x
    shapeLayoutY = maskShapeNode.y
    shapeLayoutWidth = maskShapeNode.width
    shapeLayoutHeight = maskShapeNode.height
  }
  const pivotXPdef = (followLayoutX - shapeLayoutX) / shapeLayoutWidth + 0.5
  const pivotYPdef = (followLayoutY - shapeLayoutY) / shapeLayoutHeight + 0.5

  if (followLayout && followLayout.layout) {
    followLayoutX = getPropByLayoutString('x', followLayout.layout.ldef)
    followLayoutY = getPropByLayoutString('y', followLayout.layout.ldef)
  } else {
    followLayoutX = followElementNode.x
    followLayoutY = followElementNode.y
  }
  if (shapeLayout && shapeLayout.layout) {
    shapeLayoutX = getPropByLayoutString('x', shapeLayout.layout.ldef)
    shapeLayoutY = getPropByLayoutString('y', shapeLayout.layout.ldef)
    shapeLayoutWidth = getPropByLayoutString('width', shapeLayout.layout.ldef)
    shapeLayoutHeight = getPropByLayoutString('height', shapeLayout.layout.ldef)
  } else {
    shapeLayoutX = maskShapeNode.x
    shapeLayoutY = maskShapeNode.y
    shapeLayoutWidth = maskShapeNode.width
    shapeLayoutHeight = maskShapeNode.height
  }
  const pivotXLdef = (followLayoutX - shapeLayoutX) / shapeLayoutWidth + 0.5
  const pivotYLdef = (followLayoutY - shapeLayoutY) / shapeLayoutHeight + 0.5

  const ans = {
    pdef: {
      pivotX: pivotXPdef,
      pivotY: pivotYPdef,
    },
    ldef: {
      pivotX: pivotXLdef,
      pivotY: pivotYLdef,
    },
  }
  return ans
}

export const maskSaveHandler = (nodes: qc.Node[]) => {
  nodes.forEach((node) => {
    if (isMaskGroupNode(node)) {
      const maskGroupComponent = node.getScript(scripts.maskGroup.name)
      maskGroupComponent.followElementPivotMapping =
        calFollowElementPivot(maskGroupComponent)
      node.children.forEach((child) => {
        removeUnSplitProInLayout(child)
      })
    }
  })
}

export const hasMaskableLayer = (node: qc.Node): boolean => {
  if (!node) {
    return false
  }
  const maskGroup = node.getScript(scripts.maskGroup.name)
  return maskGroup.maskGroupLayer.children.length > 0
}

// 添加遮罩组件资源
export function addMaskResource(config: {
  targetNode: qc.Node
  rename?: string
  callback: any
}) {
  const data = {
    poolTabList: ['resourcePool', 'commonResource'],
    playComponentResource: true,
    currentTab: libTabNameList.PROJECT,
    currentChildTab: '',
    rules: ['aImage', 'image', 'effect'],
    ...config,
    targetNode: {
      uuid: config.targetNode.uuid,
    },
  }
  if (config.rename) {
    data.rename = config.rename
  }
  if (config.callback) {
    data.callback = config.callback
  }
  store.commit('workspace/resourceManager/closeLibDialog')
  store.commit('workspace/resourceManager/updatePoolTabData', data)
  store.commit('workspace/resourceManager/openDialog', {
    category: 'commonResource',
    action: 'add',
  })
  store.commit('workspace/resourceManager/moveLibDialogNextToRight')
}

// 更新遮罩组件资源
export function updateMaskResource(node: qc.Node, callback: any) {
  const updateNode = node || store.state.workspace.game.activeNode
  const getCategory = (node: qc.Node) => {
    if (isImage(node)) {
      return categoryList.image.category
    } else if (isSequenceFrame(node)) {
      // 暂不支持序列帧节点资源替换
      return categoryList.aImage.category
    }
    return categoryList.image.category
  }
  const category = getCategory(updateNode)

  const onReplaced = () => {
    console.log('替换回调')
    //
    callback && callback()
  }
  const data = {
    targetNode: {
      uuid: node.uuid,
    },
  }
  store.commit('workspace/resourceManager/assignPoolTabData', data)
  store.commit('workspace/resourceManager/openDialog', {
    category: category,
    action: 'update',
    onReplaced,
  })
  store.dispatch('workspace/resourceManager/getComponents', {
    category: category,
  })
  store.commit('workspace/resourceManager/moveDialogToRight')
}

// 遮罩组件屏蔽group级的子节点的画布操作
export const maskComponentCanvasHandler = (node: qc.Node) => {
  // 也要延迟一阵，不知道哪里又把shape的visible设为true了
  setTimeout(() => {
    const maskNode = getMaskComponentNodeByChildNode(node)
    if (!maskNode) return
    let shape = getShapeByNode(maskNode)
    if (shape) {
      shape.visible(false)
    }
    const children = getDescendants(maskNode)
    children.forEach((child) => {
      // 形状节点可以被选中
      if (isMaskShapeNode(child)) {
        return
      }
      // 组和图形节点和辅助画图节点不能选中
      if (
        isGroup(child) ||
        isGraphicsNode(child) ||
        isMaskShapeAidNode(child)
      ) {
        shape = getShapeByNode(child)
        if (shape) {
          shape.visible(false)
        }
      }
    })
    const sceneNode = getSceneNodeByNode(node)
    if (sceneNode) {
      shape = getShapeByNode(sceneNode)
      if (shape) {
        shape.visible(false)
      }
    }
  }, 20)
}

export async function addMaskSubGroup(data: {
  maskGroup: qc.Node
  name: string
}) {
  const newGroup = await store.dispatch('workspace/game/addGroup', {
    nodes: [],
    targetNode: data.maskGroup,
    index: data.maskGroup.children.length - 1,
    name: data.name,
  })
  EventBus.$emit(Event.REFRESHLAYER)
  // resetSplitProp(newGroup)
}

export function updateMaskCustomSubGroup(nodes: qc.Node[], type: string) {
  if (!checkIsSupportMask()) return
  const iframeWindow = gameIframe.iframe
  if (!(iframeWindow && iframeWindow.ps && iframeWindow.ps.MaskGroup)) {
    return
  }
  const isBlankProject = store.state.workspace.project.isBlankProject
  nodes.forEach((node) => {
    if (!node.parent) return
    const maskGroupComponent = node.parent.getScript(scripts.maskGroup.name)
    if (!maskGroupComponent) return
    if (node) {
      if (type === 'add') {
        maskGroupComponent.customSubGroup.push(node)
        if (isBlankProject) {
          maskGroupComponent.customSubGroupBlankProject.push(node)
        }
      }
      if (type === 'remove') {
        maskGroupComponent.customSubGroup =
          maskGroupComponent.customSubGroup.filter(
            (item) => item.uuid !== node.uuid,
          )
        if (isBlankProject) {
          maskGroupComponent.customSubGroupBlankProject =
            maskGroupComponent.customSubGroupBlankProject.filter(
              (item) => item.uuid !== node.uuid,
            )
        }
      }
    }
  })
}
// 是否pt模板自带的自定义分组
export const isPtProjectOriCustomSubGroup = (node: qc.Node): boolean => {
  if (!node || !node.parent) return false
  const maskGroupComponent = node.parent.getScript(scripts.maskGroup.name)
  return maskGroupComponent.customSubGroupBlankProject.some(
    (cnode: qc.Node) => cnode.uuid === node.uuid,
  )
}

export const drawCircle = (node: qc.Node) => {
  const bitmapData = gameIframe.qcGame.phaser.make.bitmapData(
    node.width,
    node.height,
  )
  const context = bitmapData.context
  // 设置圆的属性
  const centerX = node.width / 2 // 圆心的x坐标
  const centerY = node.height / 2 // 圆心的y坐标
  const radiusX = node.width / 2 // 水平半径
  const radiusY = node.height / 2 // 垂直半径
  const color = 'rgba(243, 173, 2, 0.5)' // 圆的颜色

  // 绘制圆
  context.beginPath()
  context.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI)
  context.fillStyle = color // 设置圆的填充颜色，此处为透明
  context.fill() // 填充圆的内部
  node.phaser.loadTexture(bitmapData)
  node.x = 0
  node.y = 0
}

export const drawRect = (node: qc.Node) => {
  const bitmapData = gameIframe.qcGame.phaser.make.bitmapData(
    node.width,
    node.height,
  )
  const context = bitmapData.context
  // 设置矩形的属性
  const x = 0 // 矩形左上角的x坐标
  const y = 0 // 矩形左上角的y坐标
  const width = node.width // 矩形的宽度
  const height = node.height // 矩形的高度
  const color = 'rgba(243, 173, 2, 0.5)' // 矩形的颜色

  // 绘制矩形
  context.fillStyle = color // 设置矩形的填充颜色
  context.fillRect(x, y, width, height) // 使用fillRect方法绘制矩形
  node.phaser.loadTexture(bitmapData)
  node.x = 0
  node.y = 0
}
// 清空canvas画图
export const clearShape = (node: qc.Node) => {
  node.phaser.loadTexture(null)
}

export const i18nMaskGroup = (uuid: string) => {
  const node = nodePool.find(uuid)
  if (!node) return ''
  const label =
    i18n.t('workbench.mask.layer.maskGroup') + ' ' + node.name.split(' ')[1]
  return label
}

export const i18nMaskSubGroup = (uuid: string) => {
  const node = nodePool.find(uuid)
  if (!node) return ''
  let groupName = node.parent.name
  groupName =
    i18n.t('workbench.mask.layer.maskGroup') + ' ' + groupName.split(' ')[1]
  const subGroupName = isMaskedGroupNode(node)
    ? i18n.t('workbench.mask.layer.maskedGroup')
    : node.name
  const label = groupName + '-' + subGroupName
  return label
}

// 启用禁用遮罩组
export function getGroupEnableAction(value) {
  return {
    execute: ({ newValue }) => {
      const { groupNode, isEnable } = newValue
      groupNode.getScript(scripts.maskGroup.name).isEnable = isEnable
    },
    undo: async ({ oldValue }) => {
      const { groupNode, isEnable } = oldValue
      groupNode.getScript(scripts.maskGroup.name).isEnable = isEnable
    },
    value,
    methodName: 'getGroupEnableAction',
  }
}

// 启用禁用遮罩组
export function getMaskEnableAction(value) {
  return {
    execute: ({ newValue }) => {
      const { maskNode, isEnable } = newValue
      maskNode.getScript(scripts.mask.name).isEnable = isEnable
    },
    undo: async ({ oldValue }) => {
      const { maskNode, isEnable } = oldValue
      maskNode.getScript(scripts.mask.name).isEnable = isEnable
    },
    value,
    methodName: 'getMaskEnableAction',
  }
}

// 启用禁用遮罩组
export function getMaskShapeEnableAction(value) {
  return {
    execute: ({ newValue }) => {
      const { groupNode, isEnable } = newValue
      const mg = groupNode.getScript(scripts.maskGroup.name)
      mg.isEnableMaskShape = isEnable
      if (isEnable) {
        store.dispatch('workspace/game/updateActiveNode', {
          node: mg.maskShape,
        })
      }
      EventBus.$emit(Event.MASK_SHAPE_UPDATE, groupNode)
    },
    undo: async ({ oldValue }) => {
      const { groupNode, isEnable } = oldValue
      const mg = groupNode.getScript(scripts.maskGroup.name)
      mg.isEnableMaskShape = isEnable
      if (isEnable) {
        store.dispatch('workspace/game/updateActiveNode', {
          node: mg.maskShape,
        })
      }
      EventBus.$emit(Event.MASK_SHAPE_UPDATE, groupNode)
    },
    value,
    methodName: 'getMaskShapeEnableAction',
  }
}

// 新建遮罩组
export function addMaskGroup(maskNode: qc.Node) {
  const gameOb = gameIframe.qcGame
  const isBlankProject = store.state.workspace.project.isBlankProject
  // 遮罩组
  const maskGroup = gameOb.add.node(maskNode)
  maskGroup.name = 'Group ' + maskNode.children.length
  let editorData = editorScript.getScript(maskGroup)
  editorData.$data.nodeType = nodeTypes.group
  const maskGroupComponent = maskGroup.addScript(scripts.maskGroup.name)
  editorData.$data.createFromPSScripts = [scripts.maskGroup.name]
  if (isBlankProject) {
    maskGroupComponent.isCreatedByBlankProject = true
  } else {
    maskGroupComponent.isCreatedByBlankProject = false
  }
  maskGroupComponent.isEnable = true
  // 被遮罩组
  const maskedGroup = gameOb.add.node(maskGroup)
  maskedGroup.name = 'maskedGroup'
  editorData = editorScript.getScript(maskedGroup)
  editorData.$data.nodeType = nodeTypes.group
  maskGroupComponent.maskGroupLayer = maskedGroup
  // 遮罩形状
  const maskShapeGroup = gameOb.add.node(maskGroup)
  maskShapeGroup.name = 'maskShapeGroup'
  editorData = editorScript.getScript(maskShapeGroup)
  editorData.$data.nodeType = nodeTypes.group
  maskGroupComponent.maskShapeLayer = maskShapeGroup
  // 形状组
  const maskShape = gameOb.add.node(maskShapeGroup)
  maskShape.name = 'maskShape'
  maskShape.width = 200
  maskShape.height = 200
  editorData = editorScript.getScript(maskShape)
  editorData.$data.nodeType = nodeTypes.group
  maskGroupComponent.maskShape = maskShape
  store.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
    node: maskShape,
  })
  // 编辑器辅助形状节点
  const aidShapeNode = gameOb.add.image(maskShape)
  aidShapeNode.name = 'aidShape'
  aidShapeNode.width = maskShape.width
  aidShapeNode.height = maskShape.height
  aidShapeNode.visible = true
  // 跟随元素组
  const followGroup = gameOb.add.node(maskShapeGroup)
  followGroup.name = 'followGroup'
  editorData = editorScript.getScript(followGroup)
  editorData.$data.nodeType = nodeTypes.group
  maskGroupComponent.followElement = followGroup
  store.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
    node: followGroup,
  })

  executeAction({
    action: getAddNodeAction({
      newValue: {
        node: maskGroup,
        parent: maskNode,
        index: maskNode.getChildIndex(maskGroup),
      },
      oldValue: {
        node: maskGroup,
        activeUuid: maskNode.uuid,
      },
    }),
  })
}
// 游戏初始化时遮罩的初始化，显示橙色模拟图层，关掉预览遮罩效果
export function maskPsInit(maskGroupNode: qc.Node) {
  const mg = maskGroupNode.getScript(scripts.maskGroup.name)
  const aidShapeNode = getMaskShapeAidNode(maskGroupNode)
  if (!aidShapeNode) return
  aidShapeNode.visible = true
  if (mg.maskShapeType === MaskShape.ROUND) {
    drawCircle(aidShapeNode)
  } else {
    drawRect(aidShapeNode)
  }
  mg.disabledMask()
}
